import { useMutation, useQueryClient } from "@tanstack/react-query";
import { API_ROUTES } from "../../constants";
import { apiClient } from "./apiClient";
import { QUERY_KEYS } from "./constants";
import type { DeleteResponse, ChatSession } from "./types";

/**
 * Hook to delete all chat sessions for the current user
 */
export const useDeleteAllSessions = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const response = await apiClient.delete<DeleteResponse>(
        API_ROUTES.omniSearch.chats
      );
      return response.data;
    },
    // before the mutation has been sent
    onMutate: async () => {
      // Cancel any outgoing fetches for getSessions
      await queryClient.cancelQueries({
        queryKey: QUERY_KEYS.getSessions,
      });

      // Snapshot the previous value
      const previousSessions = queryClient.getQueryData<ChatSession[]>(
        QUERY_KEYS.getSessions
      );

      // Optimistically clear all sessions
      queryClient.setQueryData<ChatSession[]>(QUERY_KEYS.getSessions, []);

      return { previousSessions };
    },

    // rollback on error
    onError: (_, __, context) => {
      if (context?.previousSessions) {
        queryClient.setQueryData(
          QUERY_KEYS.getSessions,
          context.previousSessions
        );
      }
    },

    // refetch after mutation
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.getSessions });
    },
  });
};
